import { Box, IconButton, SxProps, Tooltip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { dprStatusMessages } from "src/constants/common";

const COLLAPSED_MAX_HEIGHT = 340;
const COLLAPSE_AFTER = 3;
const TIMELINE_GREEN = "#2E7D32";
const TIMELINE_GREY = "#5F6368";
const RESPONSE_LABEL = "Response Submitted";

export type StatusHistoryRecord = {
  id: string;
  status?: string | null;
  oldStatus?: string | null;
  changedByRole?: string | null;
  query?: string | null;
  created_at?: string;
  user?: {
    firstName?: string;
    lastName?: string;
  } | null;
};

type StatusHistoryProps = {
  records?: StatusHistoryRecord[] | null;
  title?: string;
  hideTitle?: boolean;
  onExport?: (records: StatusHistoryRecord[]) => void;
  exportLabel?: string;
  responseLabel?: string;
  maxWidth?: string | Record<string, string>;
  sx?: SxProps;
};

const STATUS_LABELS: Record<string, string> = {
  ...dprStatusMessages,
  NODAL_VERIFICATION_PENDING: "SNO Verification",
  VERIFICATION_PENDING: "Admin Verification",
  NODAL_UNDER_QUERY: "SNO Query",
  UNDER_QUERY: "Admin Query",
  APPROVED: "Approved",
  REJECTED: "Rejected",
  WITHDRAWN: "Withdrawal Block",
};

const formatStatus = (status?: string | null) => {
  if (!status) return "";
  if (STATUS_LABELS[status]) return STATUS_LABELS[status];
  return status
    .split("_")
    .map(
      (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");
};

const formatDateTime = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const meridiem = hours24 >= 12 ? "PM" : "AM";
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1,
  )}/${date.getFullYear()}, ${pad(hours12)}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )} ${meridiem}`;
};

const formatActor = (record: StatusHistoryRecord) => {
  const name = [record.user?.firstName, record.user?.lastName]
    .filter(Boolean)
    .join(" ");
  return [name, record.changedByRole].filter(Boolean).join(" · ");
};

const StatusHistory = ({
  records,
  title = "Status History",
  hideTitle = false,
  onExport,
  exportLabel = "Export status history",
  responseLabel = RESPONSE_LABEL,
  maxWidth = { xs: "100%", lg: "60%" },
  sx,
}: StatusHistoryProps) => {
  const [expanded, setExpanded] = useState(false);

  const history: StatusHistoryRecord[] = useMemo(
    () =>
      (Array.isArray(records)
        ? records.filter((record) => record?.status || record?.query)
        : []
      ).sort(
        (a, b) =>
          new Date(b.created_at ?? 0).getTime() -
          new Date(a.created_at ?? 0).getTime()
      ),
    [records]
  );

  if (history.length === 0) return null;

  const collapsible = history.length > COLLAPSE_AFTER;
  const collapsed = collapsible && !expanded;

  return (
    <Box
      sx={[
        {
          margin: "20px 0",
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "primary.main",
          borderRadius: "15px",
          padding: "20px 25px",
          backgroundColor: "#fff",
          maxWidth,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {(!hideTitle || onExport) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
            marginBottom: "20px",
          }}
        >
          {!hideTitle && (
            <Typography
              component="h3"
              sx={{
                fontFamily: "Poppins",
                fontSize: "22px",
                fontWeight: "500",
                paddingBottom: "10px",
                margin: 0,
                color: "primary.main",
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: "primary.main",
              }}
            >
              {title}
            </Typography>
          )}
          {onExport && (
            <Tooltip title={exportLabel}>
              <IconButton
                onClick={() => onExport(history)}
                aria-label={exportLabel}
                sx={{
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderColor: "primary.main",
                  borderRadius: "8px",
                  color: "primary.main",
                  "&:hover": { backgroundColor: "action.hover" },
                }}
              >
                <Box
                  component="img"
                  src="/assets/svg/export.svg"
                  alt=""
                  aria-hidden="true"
                  sx={{ width: "18px", height: "18px" }}
                />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          maxHeight: collapsed ? `${COLLAPSED_MAX_HEIGHT}px` : "none",
          overflow: "hidden",
        }}
      >
        {history.map((record, index) => {
          const isLast = index === history.length - 1;
          const actor = formatActor(record);
          const isResponse = !record.status;
          const dotColor = isResponse ? TIMELINE_GREY : TIMELINE_GREEN;
          return (
            <Box key={record.id} sx={{ display: "flex", gap: "14px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: dotColor,
                    flexShrink: 0,
                    marginTop: "6px",
                  }}
                />
                {!isLast && (
                  <Box
                    sx={{
                      width: "2px",
                      flexGrow: 1,
                      backgroundColor: "custom.greyish.1",
                    }}
                  />
                )}
              </Box>

              <Box sx={{ paddingBottom: isLast ? 0 : "22px" }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: "600",
                    fontSize: "17px",
                    color: dotColor,
                  }}
                >
                  {isResponse
                    ? responseLabel
                    : record.oldStatus && record.oldStatus !== record.status
                    ? `${formatStatus(record.oldStatus)} → ${formatStatus(
                        record.status
                      )}`
                    : formatStatus(record.status)}
                </Typography>

                {(actor || record.created_at) && (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "14px",
                      color: "custom.greyish.2",
                      marginTop: "3px",
                    }}
                  >
                    {[actor, formatDateTime(record.created_at)]
                      .filter(Boolean)
                      .join(" — ")}
                  </Typography>
                )}

                {record.query && (
                  <Typography
                    sx={{
                      fontFamily: "Poppins",
                      fontSize: "17px",
                      fontWeight: "400",
                      marginTop: "6px",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {record.query}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        {collapsed && (
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "90px",
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, #fff 90%)",
              pointerEvents: "none",
            }}
          />
        )}
      </Box>

      {collapsible && (
        <Typography
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            fontFamily: "Poppins",
            fontWeight: "500",
            fontSize: "14px",
            color: "primary.main",
            textAlign: "center",
            cursor: "pointer",
            marginTop: "6px",
            userSelect: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {expanded ? "View Less ▲" : "View More ▼"}
        </Typography>
      )}
    </Box>
  );
};

export default StatusHistory;
